import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Container,
  Avatar,
  IconButton,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import LanguageIcon from '@mui/icons-material/Language';
import PersonIcon from '@mui/icons-material/Person';
import { Helmet } from 'react-helmet';
import Toast from '../components/Toast';
import { useUser } from "../context/UserContext";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 4 }}>{children}</Box>}
    </div>
  );
}

const MyProfile = () => {
  const [tab, setTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [editOrgDetails, setEditOrgDetails] = useState([]);
  const [editUser, setEditUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef();
  const [editOrg, setEditOrg] = useState({});
  const [editOrgMode, setEditOrgMode] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';

  const { user } = useUser();


  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}my-profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
          setToast({ open: true, message: data.message || 'Failed to load profile', severity: "error" });
          
        } else {
          setProfileUser(data.data);
          setOrg(data.data.origanisation);
          setEditOrgDetails(data.data.edit_org_table || [])
          setEditUser(data.data);
          setPreviewUrl(data.data.profile_pic || 'https://i.pravatar.cc/150');
        }
      } catch (err) {
        setToast({ open: true, message: 'Network error. Please try again.', severity: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleEdit = () => {
    setEditUser(profileUser);
    setEditMode(true);
    setPreviewUrl(profileUser?.profile_pic || 'https://i.pravatar.cc/150');
    setSelectedImage(null);
  };

  const handleEditOrg = () => {
    setEditOrg(org);
    setEditOrgMode(true);
  };

  const handleEditChange = (e) => {
    setEditUser({ ...editUser, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && !file.type.startsWith('image/')) {
      setToast({ open: true, message: 'Please select a valid image file.', severity: "error" });
      return;
    }
    if (file && file.size > 200 * 1024) {
      setToast({ open: true, message: 'Image size should be less than 200 KB.', severity: "error" });
      return;
    }

    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleEditSave = async () => {
    try {
      if (!editUser.first_name || !editUser.last_name) {
        setToast({ open: true, message: 'First name and last name are required.', severity: "error" });
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('first_name', editUser.first_name || '');
      fd.append('last_name', editUser.last_name || '');
      if (selectedImage instanceof File) {
        fd.append('profile_pic', selectedImage);
      }

      const res = await fetch(`${API_URL}update-profile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const data = await res.json();
      if (!res.ok || data?.success === false) {
        return setToast({ open: true, message: data?.message || 'Failed to update profile', severity: "error" });
      }

      if (data?.data) {
        setProfileUser(data.data);
        setPreviewUrl(data.data.profile_pic || 'https://i.pravatar.cc/150');

        const storedUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const updatedUser = {
          ...storedUser,
          username: `${editUser.first_name} ${editUser.last_name}`,
          profile_pic: data.data.profile_pic || storedUser.profile_pic,
        };
        localStorage.setItem('userInfo', JSON.stringify(updatedUser));
      }

      setEditMode(false);
      setSelectedImage(null);
      setToast({ open: true, message: 'Profile updated successfully!', severity: "success" });
      window.location.reload();

    } catch (e) {
      setToast({ open: true, message: 'Network error. Please try again.', severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditMode(false);
    setSelectedImage(null);
    setPreviewUrl(profileUser?.profile_pic || 'https://i.pravatar.cc/150');
  };

  const handleEditOrgCancel = () => {
    setEditOrgMode(false);
  };

  const handleEditOrgChange = (e) => {
    setEditOrg({ ...editOrg, [e.target.name]: e.target.value });
  };

  const handleEditOrgSave = async () => {
    try {
      if (!editOrg.org_name) {
        setToast({ open: true, message: 'Organization name is required.', severity: "error" });
        return;
      }
      if (!editOrg.address) {
        setToast({ open: true, message: 'Organization address is required.', severity: "error" });
        return;
      }
      if (!editOrg.contact_person_name) {
        setToast({ open: true, message: 'Organization contact person name is required.', severity: "error" });
        return;
      }
      if (!/\S+@\S+\.\S+/.test(editOrg.email)) {
        setToast({ open: true, message: 'Enter a valid email address.', severity: "error" });
        return;
      }
      if (!/^\+?[0-9\s\-()]{7,15}$/.test(editOrg.phone)) {
        setToast({ open: true, message: 'Enter a valid phone number.', severity: "error" });
        return;
      }

      setLoading(true);

      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('org_name', editOrg.org_name || '');
      fd.append('address', editOrg.address || '');
      fd.append('contact_person_name', editOrg.contact_person_name || '');
      fd.append('email', editOrg.email || '');
      fd.append('phone', editOrg.phone || '');
      fd.append('website', editOrg.website || '');
      if (editOrg.logo instanceof File) {
        fd.append('logo', editOrg.logo);
      }

      const res = await fetch(`${API_URL}update-organization`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const data = await res.json();
      if (!res.ok || data?.success === false) {
        return setToast({ open: true, message: data?.message || 'Failed to update organization', severity: "error" });
      }
      if (data?.organization) {
        setOrg(data.organization);
        setEditOrgDetails(data.organization.edit_org_table || [])
      }
      setEditOrgMode(false);
      setToast({ open: true, message: 'Organization updated successfully!', severity: "success" });
    } catch (e) {
      setToast({ open: true, message: 'Network error. Please try again.', severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '70vh',
    }}>
      <Typography variant="h5" sx={{
        color: '#4CA1AF',
        fontWeight: 600,
      }}>
        Loading...
      </Typography>
    </Box>
  );

  const InfoCard = ({ icon, label, value, newValue }) => (
    <Box sx={{
      p: 3,
      borderRadius: 2,
      background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.05) 0%, rgba(44, 62, 80, 0.05) 100%)',
      border: '1px solid rgba(76, 161, 175, 0.2)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(76, 161, 175, 0.2)',
        borderColor: 'rgba(76, 161, 175, 0.4)',
      },
    }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <Box sx={{
          color: '#4CA1AF',
          display: 'flex',
          alignItems: 'center',
        }}>
          {icon}
        </Box>
        <Typography variant="caption" sx={{
          color: 'rgba(255, 255, 255, 0.6)',
          textTransform: 'uppercase',
          letterSpacing: 1,
          fontWeight: 600,
        }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{
        color: '#FFFFFF',
        fontWeight: 500,
        fontSize: '1.1rem',
        ml: 5,
      }}>
        {value || 'Not provided'}
      </Typography>
      {newValue && (
        <Typography variant="body2" sx={{
          color: '#4CA1AF',
          mt: 1.5,
          ml: 5,
          fontStyle: 'italic',
          fontSize: '0.95rem',
        }}>
          Pending: {newValue}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>My Profile - GPN</title>
      </Helmet>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1,
            }}
          >
            My Profile
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            Manage your personal information and organization details
          </Typography>
        </Box>

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            border: '1px solid rgba(76, 161, 175, 0.15)',
            overflow: 'hidden',
          }}
        >
          {/* Tabs */}
          <Box sx={{
            borderBottom: '1px solid rgba(76, 161, 175, 0.1)',
            px: 4,
            pt: 3,
          }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  textTransform: 'none',
                  minHeight: 56,
                  px: 3,
                  '&.Mui-selected': {
                    color: '#4CA1AF',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: 'linear-gradient(90deg, #4CA1AF 0%, #2C3E50 100%)',
                },
              }}
            >
              <Tab label="Personal Information" />
              {user?.role !== 1 && <Tab label="Organization" />}
            </Tabs>
          </Box>

          {/* Profile Tab */}
          <TabPanel value={tab} index={0}>
            <Box sx={{ px: 4, pb: 4 }}>
              {!editMode ? (
                <>
                  {/* Profile Header */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'center', md: 'flex-start' },
                    gap: 4,
                    mb: 5,
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.08) 0%, rgba(44, 62, 80, 0.08) 100%)',
                    border: '1px solid rgba(76, 161, 175, 0.15)',
                  }}>
                    <Avatar
                      src={profileUser?.profile_pic || 'https://i.pravatar.cc/150'}
                      alt={profileUser?.first_name}
                      sx={{
                        width: 140,
                        height: 140,
                        border: '5px solid rgba(76, 161, 175, 0.3)',
                        boxShadow: '0 8px 32px rgba(76, 161, 175, 0.3)',
                      }}
                    />
                    <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                      <Typography variant="h4" sx={{
                        color: '#FFFFFF',
                        fontWeight: 700,
                        mb: 1,
                      }}>
                        {profileUser?.first_name} {profileUser?.last_name}
                      </Typography>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        sx={{ mb: 2 }}
                        justifyContent={{ xs: 'center', md: 'flex-start' }}
                      >
                        <Chip
                          icon={<EmailIcon sx={{ fontSize: 18 }} />}
                          label={profileUser?.email}
                          sx={{
                            background: 'rgba(76, 161, 175, 0.1)',
                            color: '#4CA1AF',
                            border: '1px solid rgba(76, 161, 175, 0.3)',
                            fontWeight: 500,
                          }}
                        />
                        {profileUser?.phone && (
                          <Chip
                            icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                            label={profileUser?.phone}
                            sx={{
                              background: 'rgba(76, 161, 175, 0.1)',
                              color: '#4CA1AF',
                              border: '1px solid rgba(76, 161, 175, 0.3)',
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </Stack>
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEdit}
                        sx={{
                          mt: 2,
                          py: 1.5,
                          px: 4,
                          fontSize: '1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                          boxShadow: '0 4px 20px rgba(76, 161, 175, 0.4)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                            boxShadow: '0 6px 30px rgba(76, 161, 175, 0.6)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Edit Profile
                      </Button>
                    </Box>
                  </Box>

                  {/* Profile Details */}
                  <Typography variant="h6" sx={{
                    color: '#FFFFFF',
                    fontWeight: 600,
                    mb: 3,
                  }}>
                    Contact Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<EmailIcon />}
                        label="Email Address"
                        value={profileUser?.email}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<PhoneIcon />}
                        label="Phone Number"
                        value={profileUser?.phone}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Box>
                  {/* Edit Mode */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 5,
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.08) 0%, rgba(44, 62, 80, 0.08) 100%)',
                    border: '1px solid rgba(76, 161, 175, 0.15)',
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      onChange={handleImageChange}
                    />
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Avatar
                        src={previewUrl}
                        alt={editUser?.first_name}
                        sx={{
                          width: 140,
                          height: 140,
                          border: '5px solid rgba(76, 161, 175, 0.3)',
                          boxShadow: '0 8px 32px rgba(76, 161, 175, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 12px 40px rgba(76, 161, 175, 0.5)',
                          },
                        }}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      />
                      <IconButton
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                          color: '#FFFFFF',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                          },
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        <PhotoCamera />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      textAlign: 'center',
                    }}>
                      Click to upload new photo (Max 200KB)
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="First Name"
                        name="first_name"
                        value={editUser?.first_name || ''}
                        onChange={handleEditChange}
                        fullWidth
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Last Name"
                        name="last_name"
                        value={editUser?.last_name || ''}
                        onChange={handleEditChange}
                        fullWidth
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleEditSave}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        px: 5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                        boxShadow: '0 4px 20px rgba(76, 161, 175, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                          boxShadow: '0 6px 30px rgba(76, 161, 175, 0.6)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleEditCancel}
                      sx={{
                        py: 1.5,
                        px: 5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        borderColor: 'rgba(76, 161, 175, 0.5)',
                        color: '#4CA1AF',
                        '&:hover': {
                          borderColor: '#4CA1AF',
                          background: 'rgba(76, 161, 175, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Organization Tab */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ px: 4, pb: 4 }}>
              {!editOrgMode ? (
                <>
                  {/* Organization Header */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'center', md: 'flex-start' },
                    gap: 4,
                    mb: 5,
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.08) 0%, rgba(44, 62, 80, 0.08) 100%)',
                    border: '1px solid rgba(76, 161, 175, 0.15)',
                  }}>
                    <Avatar
                      src={org?.logo || 'https://i.pravatar.cc/150'}
                      alt={org?.org_name}
                      sx={{
                        width: 140,
                        height: 140,
                        border: '5px solid rgba(76, 161, 175, 0.3)',
                        boxShadow: '0 8px 32px rgba(76, 161, 175, 0.3)',
                      }}
                    />
                    <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                      <Typography variant="h4" sx={{
                        color: '#FFFFFF',
                        fontWeight: 700,
                        mb: 2,
                      }}>
                        {org?.org_name}
                      </Typography>
                      {org?.update_request === 'Requested' && (
                        <Chip
                          label="Update Request Pending"
                          sx={{
                            mb: 2,
                            background: 'rgba(252, 165, 165, 0.15)',
                            color: '#FCA5A5',
                            border: '1px solid rgba(252, 165, 165, 0.3)',
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {org?.update_request !== 'Requested' && (
                        <Button
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={handleEditOrg}
                          sx={{
                            mt: 1,
                            py: 1.5,
                            px: 4,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                            boxShadow: '0 4px 20px rgba(76, 161, 175, 0.4)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                              boxShadow: '0 6px 30px rgba(76, 161, 175, 0.6)',
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          Edit Organization
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Organization Details */}
                  <Typography variant="h6" sx={{
                    color: '#FFFFFF',
                    fontWeight: 600,
                    mb: 3,
                  }}>
                    Organization Details
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<BusinessIcon />}
                        label="Organization Name"
                        value={org?.org_name}
                        newValue={editOrgDetails[0]?.org_name}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<PersonIcon />}
                        label="Contact Person"
                        value={org?.contact_person_name}
                        newValue={editOrgDetails[0]?.contact_person_name}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<EmailIcon />}
                        label="Email Address"
                        value={org?.email}
                        newValue={editOrgDetails[0]?.email}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<PhoneIcon />}
                        label="Phone Number"
                        value={org?.phone}
                        newValue={editOrgDetails[0]?.phone}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<LanguageIcon />}
                        label="Website"
                        value={org?.website}
                        newValue={editOrgDetails[0]?.website}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoCard
                        icon={<BusinessIcon />}
                        label="Address"
                        value={org?.address}
                        newValue={editOrgDetails[0]?.address}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Box>
                  {/* Edit Organization Mode */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 5,
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.08) 0%, rgba(44, 62, 80, 0.08) 100%)',
                    border: '1px solid rgba(76, 161, 175, 0.15)',
                  }}>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file) {
                          setEditOrg(prev => ({
                            ...prev,
                            logo: file,
                            logoPreview: URL.createObjectURL(file),
                          }));
                        }
                      }}
                    />
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Avatar
                        src={editOrg?.logoPreview ? editOrg.logoPreview : (org?.logo || 'https://i.pravatar.cc/150')}
                        alt={editOrg?.org_name}
                        sx={{
                          width: 140,
                          height: 140,
                          border: '5px solid rgba(76, 161, 175, 0.3)',
                          boxShadow: '0 8px 32px rgba(76, 161, 175, 0.3)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 12px 40px rgba(76, 161, 175, 0.5)',
                          },
                        }}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      />
                      <IconButton
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                          color: '#FFFFFF',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                          },
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        <PhotoCamera />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" sx={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      textAlign: 'center',
                    }}>
                      Click to upload organization logo
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Organization Name"
                        name="org_name"
                        value={editOrg?.org_name || ''}
                        onChange={handleEditOrgChange}
                        fullWidth
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Contact Person"
                        name="contact_person_name"
                        value={editOrg?.contact_person_name || ''}
                        onChange={handleEditOrgChange}
                        fullWidth
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        value={editOrg?.email || ''}
                        onChange={handleEditOrgChange}
                        fullWidth
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone"
                        name="phone"
                        value={editOrg?.phone || ''}
                        onChange={handleEditOrgChange}
                        fullWidth
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Website"
                        name="website"
                        value={editOrg?.website || ''}
                        onChange={handleEditOrgChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Address"
                        name="address"
                        value={editOrg?.address || ''}
                        onChange={handleEditOrgChange}
                        fullWidth
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#FFFFFF',
                            background: 'rgba(255, 255, 255, 0.05)',
                            height: '56px',
                            '& fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.3)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(76, 161, 175, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4CA1AF',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleEditOrgSave}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        px: 5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                        boxShadow: '0 4px 20px rgba(76, 161, 175, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5FB1C1 0%, #3A5A6E 100%)',
                          boxShadow: '0 6px 30px rgba(76, 161, 175, 0.6)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleEditOrgCancel}
                      sx={{
                        py: 1.5,
                        px: 5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        borderColor: 'rgba(76, 161, 175, 0.5)',
                        color: '#4CA1AF',
                        '&:hover': {
                          borderColor: '#4CA1AF',
                          background: 'rgba(76, 161, 175, 0.1)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </TabPanel>
        </Paper>
      </Container>
      <Toast toast={toast} setToast={setToast} />
    </>
  );
};

export default MyProfile;
